import { useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useGetList, useNotify, useRefresh, useUpdate } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { AlertType, Reimbursement, ResourceName, StoplossClaim } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { reimbursementDate } from "@/resources/clients/components/claims-summary/utils/reportingPeriod";

/**
 * Link one of the claimant's unlinked reimbursements to this claim (#7585).
 * The reverse of the reimbursement page's "Link claim" card: same PUT, same
 * field (`claim_id_ref`), from the claim's side.
 */
export const LinkReimbursementDialog = ({
  open,
  onClose,
  claim,
  claimantId,
}: {
  open: boolean;
  onClose: () => void;
  claim: StoplossClaim;
  claimantId?: string;
}) => {
  const [update, { isPending }] = useUpdate();
  const refresh = useRefresh();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const [chosen, setChosen] = useState<Reimbursement | null>(null);

  const { data = [], isLoading } = useGetList<Reimbursement>(
    ResourceName.REIMBURSEMENTS,
    {
      filter: { claimant_id_ref: claimantId },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "check_date", order: "DESC" },
    },
    { enabled: open && !!claimantId }
  );
  const unlinked = useMemo(() => data.filter((r) => !r.claim_id_ref && !r.stoploss_claim_id_ref), [data]);

  const label = (r: Reimbursement) =>
    [
      formatLongDate(reimbursementDate(r) ?? undefined),
      typeof r.reimbursement_amount === "number" ? formatUSD(r.reimbursement_amount) : "—",
      formatToTitleCase(r.status || ""),
      r.payment_reference || r.check_number || "",
    ]
      .filter(Boolean)
      .join(" — ");

  const save = async () => {
    if (!chosen) return;
    try {
      await update(
        ResourceName.REIMBURSEMENTS,
        { id: chosen.id, data: { claim_id_ref: claim.id }, previousData: chosen },
        { returnPromise: true }
      );
      notify("Reimbursement linked to this claim", { type: AlertType.SUCCESS });
      refresh();
      setChosen(null);
      onClose();
    } catch (e) {
      notifyError(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link a reimbursement</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reimbursements for this claimant that are not yet linked to a claim.
        </Typography>
        <Autocomplete
          options={unlinked}
          loading={isLoading}
          getOptionLabel={label}
          value={chosen}
          onChange={(_, v) => setChosen(v)}
          noOptionsText={claimantId ? "No unlinked reimbursements for this claimant" : "The claim has no claimant"}
          renderInput={(params) => <TextField {...params} label="Reimbursement" size="small" autoFocus />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void save()} disabled={!chosen || isPending} disableElevation>
          Link
        </Button>
      </DialogActions>
    </Dialog>
  );
};
